<script setup lang="ts">
/**
 * Per-tier rows (plan §4.4, `perTier` parameter; staggered family): for each tier, top to bottom
 * as it stands, the end panels, posts and back block. Each change goes through the family's
 * `setParam` (src/app/edits.ts → setFamilyParam), which refuses a layout that leaves a shelf
 * unsupported or over the cantilever limit.
 */
import { computed } from 'vue';
import type { ParameterDef } from '@/families/types';
import Icon from './Icon.vue';
import { snap, ui } from '@/app/state';
import { setFamilyParam } from '@/app/edits';
import { focusSoon } from '@/app/navigation';

type Ends = { leftPanel: boolean; rightPanel: boolean; backBlock: 'none' | 'left-of-divider' | 'right-of-divider' | 'full'; leftPost: boolean; rightPost: boolean };
const props = defineProps<{ p: ParameterDef }>();
const ends = computed<Ends[]>(() => (((snap.value.design.params ?? {})[props.p.key] as Ends[] | undefined) ?? []));
const rows = computed(() => ends.value.map((e, i) => ({ i, e })).reverse());
const err = computed(() => ui.fieldErrors[props.p.key] ?? null);
const TOGGLES: [keyof Ends, string][] = [['leftPanel', 'Left panel'], ['leftPost', 'Left post'], ['rightPost', 'Right post'], ['rightPanel', 'Right panel']];
const BLOCKS: [Ends['backBlock'], string][] = [['none', 'None'], ['left-of-divider', 'Left'], ['right-of-divider', 'Right'], ['full', 'Both']];

function set(i: number, k: keyof Ends, v: unknown, label: string) {
  focusSoon(`[data-tier="${i}"] [data-end="${String(k)}"]${k === 'backBlock' ? `[data-value="${String(v)}"]` : ''}`);
  const next = ends.value.map((e, j) => (j === i ? { ...e, [k]: v } : e));
  setFamilyParam(props.p.key, next, `Tier ${i + 1}: ${label}`);
}
</script>

<template>
  <div class="tier-ends">
    <h3 :id="`ph-${p.key}`" class="h-sub">{{ p.ui.label }} <span class="muted">top to bottom</span></h3>
    <ol class="tiers" :aria-labelledby="`ph-${p.key}`">
      <li v-for="r in rows" :key="r.i" class="tier" :data-tier="r.i">
        <span class="op-l"><span class="op-n" aria-hidden="true">{{ r.i + 1 }}</span><span>Tier {{ r.i + 1 }}</span></span>
        <div class="seg seg--sm seg--param" role="group" :aria-label="`Tier ${r.i + 1} ends`">
          <button v-for="[k, l] in TOGGLES" :key="k" type="button" :aria-pressed="!!r.e[k]" :data-end="k" @click="set(r.i, k, !r.e[k], `${l} ${r.e[k] ? 'off' : 'on'}`)">{{ l }}</button>
        </div>
        <span class="muted small tier-sub">Back block</span>
        <div class="seg seg--sm seg--param" role="radiogroup" :aria-label="`Tier ${r.i + 1} back block`">
          <button v-for="[v, l] in BLOCKS" :key="v" type="button" role="radio" :aria-checked="r.e.backBlock === v" data-end="backBlock" :data-value="v" @click="set(r.i, 'backBlock', v, `back block ${l.toLowerCase()}`)">{{ l }}</button>
        </div>
      </li>
    </ol>
    <p v-if="err" :id="`err-${p.key}`" class="field-err" role="alert"><Icon name="alert" /><span>{{ err }}</span></p>
    <p v-if="p.ui.help" class="hint">{{ p.ui.help }}</p>
  </div>
</template>

<style scoped>
.tiers { list-style: none; margin: 0; padding: 0; display: grid; gap: 10px; }
.tier { display: flex; flex-direction: column; align-items: flex-start; gap: 4px; padding: 8px; border-radius: 14px; border: 1px solid var(--line); }
.tier-sub { margin-top: 2px; }
</style>
